import { paths } from 'src/routes/paths';

import { CONFIG } from 'src/config-global';

import { SvgColor } from 'src/components/svg-color';

const icon = (name) => <SvgColor src={`${CONFIG.assetsDir}/assets/icons/navbar/${name}.svg`} />;

const ICONS = {
  dashboard: icon('ic-dashboard'),
  user: icon('ic-user'),
  analytics: icon('ic-analytics'),
  club: icon('ic-banking'),
  facility: icon('ic-booking'),
  session: icon('ic-calendar'),
  match: icon('ic-external'),
  leaderboard: icon('ic-analytics'),
  payment: icon('ic-invoice'),
  audit: icon('ic-file'),
  settings: icon('ic-parameter'),
  app_version: icon('ic-external'),
  banner: icon('ic-label'),
  booking: icon('ic-booking'),
  helpdesk: icon('ic-chat'),
  feedback: icon('ic-mail'),
  support: icon('ic-chat'),
  bug: icon('ic-file'),
  monitor: icon('ic-external'),
  ad: icon('ic-label'),
};

/**
 * Navigation Configuration for PickleLoop Admin Panel
 *
 * Role-Based Access:
 * - ADMIN: Full access to all pages
 * - FACILITY_OWNER: Dashboard, Analytics, Clubs, Facilities, Bookings, Sessions, Payments, Matches, Managers
 * - MANAGER: Dashboard, Clubs, Facilities, Bookings, Sessions, Matches, Payments
 *
 * Items without 'roles' property are visible to all authenticated users.
 * Items with 'roles' property are only visible to specified roles.
 */
export const pickleLoopNavData = [
  /**
   * Open Play Management
   */
  {
    subheader: 'Open Play',
    items: [
      {
        title: 'Open Play',
        path: paths.dashboard.openPlay.root,
        icon: ICONS.dashboard,
        roles: ['ADMIN', 'FACILITY_OWNER', 'MANAGER'],
      },
    ],
  },
  /**
   * Dashboard Overview
   */
  {
    subheader: 'Overview',
    items: [
      {
        title: 'Dashboard',
        path: paths.dashboard.root,
        icon: ICONS.dashboard,
        // No roles - visible to all
      },
      {
        title: 'Analytics',
        path: paths.dashboard.analytics,
        icon: ICONS.analytics,
        roles: ['ADMIN', 'FACILITY_OWNER'], // Admin and Facility Owner only
      },
    ],
  },

  /**
   * Core Management
   */
  {
    subheader: 'Core Management',
    items: [
      {
        title: 'Users',
        path: paths.dashboard.users.root,
        icon: ICONS.user,
        roles: ['ADMIN'], // Admin only
        children: [{ title: 'All Users', path: paths.dashboard.users.list }],
      },
      {
        title: 'Clubs',
        path: '/dashboard/clubs',
        icon: ICONS.club,
        roles: ['ADMIN', 'FACILITY_OWNER', 'MANAGER'], // All roles
        children: [
          { title: 'All Clubs', path: '/dashboard/clubs' },
          {
            title: 'Create Club',
            path: '/dashboard/clubs/new',
            roles: ['ADMIN', 'FACILITY_OWNER'],
          },
        ],
      },
      {
        title: 'Facilities',
        path: '/dashboard/facilities',
        icon: ICONS.facility,
        roles: ['ADMIN', 'FACILITY_OWNER', 'MANAGER'], // All roles
        children: [
          { title: 'All Facilities', path: '/dashboard/facilities' },
          {
            title: 'Create Facility',
            path: '/dashboard/facilities/new',
            roles: ['ADMIN', 'FACILITY_OWNER'],
          },
          { title: 'Maintenance', path: '/dashboard/facilities?status=maintenance' },
        ],
      },
    ],
  },

  /**
   * Game Management
   */
  {
    subheader: 'Game Management',
    items: [
      {
        title: 'Bookings',
        path: paths.dashboard.bookings.root,
        icon: ICONS.booking,
        roles: ['ADMIN', 'FACILITY_OWNER', 'MANAGER'], // All roles
        children: [
          { title: 'All Bookings', path: paths.dashboard.bookings.list },
          {
            title: 'Court Bookings',
            path: `${paths.dashboard.bookings.list}?reservation_type=FACILITY_BOOKING`,
          },
          {
            title: 'Session Bookings',
            path: `${paths.dashboard.bookings.list}?reservation_type=SESSION`,
          },
        ],
      },
      {
        title: 'Sessions',
        path: '/dashboard/sessions',
        icon: ICONS.session,
        roles: ['ADMIN', 'FACILITY_OWNER', 'MANAGER'], // All roles
        children: [
          { title: 'All Sessions', path: '/dashboard/sessions' },
          { title: 'Recurring Sessions', path: '/dashboard/sessions/recurring' },
          {
            title: 'Create Session',
            path: '/dashboard/sessions/new',
            roles: ['ADMIN', 'FACILITY_OWNER'],
          },
          { title: "Today's Sessions", path: '/dashboard/sessions?date=today' },
          { title: 'Upcoming Sessions', path: '/dashboard/sessions?status=scheduled' },
        ],
      },
      {
        title: 'Matches',
        path: '/dashboard/matches',
        icon: ICONS.match,
        roles: ['ADMIN', 'FACILITY_OWNER', 'MANAGER'], // All roles
        children: [
          { title: 'All Matches', path: '/dashboard/matches' },
          { title: 'Live Matches', path: '/dashboard/matches?status=in_progress' },
          { title: 'Match Results', path: '/dashboard/matches?status=completed' },
        ],
      },
      {
        title: 'Leaderboard',
        path: '/dashboard/leaderboard',
        icon: ICONS.leaderboard,
        roles: ['ADMIN'], // Admin only
        children: [{ title: 'Recent Activity', path: '/dashboard/leaderboard?filter=recent' }],
      },
    ],
  },

  /**
   * Business Management
   */
  {
    subheader: 'Business',
    items: [
      {
        title: 'Payments',
        path: '/dashboard/payments',
        icon: ICONS.payment,
        roles: ['ADMIN', 'FACILITY_OWNER', 'MANAGER'], // All roles
        children: [
          { title: 'All Payments', path: '/dashboard/payments' },
          { title: 'Booking Payments', path: '/dashboard/payments?booking_type=FACILITY_BOOKING' },
          { title: 'Session Payments', path: '/dashboard/payments?booking_type=SESSION' },
            { title: 'Revenue Analytics', path: '/dashboard/payments/reports' },
        ],
      },
      {
        title: 'Wallet Ledger',
        path: '/dashboard/wallet',
        icon: ICONS.payment,
        roles: ['ADMIN', 'FACILITY_OWNER', 'MANAGER'],
      },
      {
        title: 'Vouchers',
        path: '/dashboard/vouchers',
        icon: ICONS.banner,
        roles: ['ADMIN', 'FACILITY_OWNER', 'MANAGER'],
        children: [
          { title: 'Campaigns', path: '/dashboard/vouchers' },
          { title: 'Redeem voucher', path: '/dashboard/vouchers/redeem' },
          { title: 'Redemptions', path: '/dashboard/vouchers/redemptions' },
        ],
      },
      {
        title: 'Banners',
        path: '/dashboard/banners',
        icon: ICONS.banner,
        roles: ['ADMIN'], // Admin only
        children: [
          { title: 'All Banners', path: '/dashboard/banners' },
          { title: 'Create Banner', path: '/dashboard/banners/new' },
        ],
      },
      {
        title: 'Monitors',
        path: paths.dashboard.monitors.root,
        icon: ICONS.monitor,
        roles: ['ADMIN', 'FACILITY_OWNER', 'MANAGER'],
        children: [{ title: 'All Monitors', path: paths.dashboard.monitors.list }],
      },
      {
        title: 'Display Ads',
        path: paths.dashboard.ads.root,
        icon: ICONS.ad,
        roles: ['ADMIN'],
        children: [
          { title: 'All Ads', path: paths.dashboard.ads.list },
          { title: 'Create Ad', path: paths.dashboard.ads.new },
          { title: 'Playlists', path: paths.dashboard.ads.playlists.root },
          { title: 'Schedules', path: paths.dashboard.ads.schedules.root },
        ],
      },
    ],
  },

  /**
   * System Management
   */
  {
    subheader: 'System',
    items: [
      {
        title: 'App Versions',
        path: '/dashboard/app-versions',
        icon: ICONS.app_version,
        roles: ['ADMIN'], // Admin only
        children: [
          { title: 'All Versions', path: '/dashboard/app-versions' },
          { title: 'Create Version', path: '/dashboard/app-versions/new' },
          { title: 'Force Updates', path: '/dashboard/app-versions?force_update=true' },
        ],
      },
    ],
  },

  /**
   * Administration
   */
  {
    subheader: 'Administration',
    items: [
      {
        title: 'Managers',
        path: '/dashboard/admin/managers',
        icon: ICONS.user,
        roles: ['ADMIN', 'FACILITY_OWNER'], // Admin and Facility Owner
        children: [
          { title: 'All Managers', path: '/dashboard/admin/managers' },
          { title: 'Create Manager', path: '/dashboard/admin/managers/new' },
        ],
      },
      {
        title: 'Facility Owners',
        path: '/dashboard/admin/facility-owners',
        icon: ICONS.user,
        roles: ['ADMIN'], // Admin only
        children: [
          { title: 'All Facility Owners', path: '/dashboard/admin/facility-owners' },
          { title: 'Create Facility Owner', path: '/dashboard/admin/facility-owners/new' },
        ],
      },
    ],
  },

  /**
   * Help Desk
   */
  {
    subheader: 'Help Desk',
    items: [
      {
        title: 'Feedback',
        path: paths.dashboard.feedback.root,
        icon: ICONS.feedback,
        roles: ['ADMIN'], // Admin only
      },
      {
        title: 'Support',
        path: paths.dashboard.support.root,
        icon: ICONS.support,
        roles: ['ADMIN'], // Admin only
        children: [
          { title: 'All Tickets', path: paths.dashboard.support.list },
          { title: 'Create Ticket', path: paths.dashboard.support.new },
          { title: 'Open Tickets', path: `${paths.dashboard.support.list}?status=OPEN` },
          { title: 'In Progress', path: `${paths.dashboard.support.list}?status=IN_PROGRESS` },
        ],
      },
      {
        title: 'Bugs',
        path: paths.dashboard.bugs.root,
        icon: ICONS.bug,
        roles: ['ADMIN'], // Admin only
        children: [
          { title: 'All Bugs', path: paths.dashboard.bugs.list },
          { title: 'Create Bug Report', path: paths.dashboard.bugs.new },
          { title: 'Open Bugs', path: `${paths.dashboard.bugs.list}?status=OPEN` },
          { title: 'In Progress', path: `${paths.dashboard.bugs.list}?status=IN_PROGRESS` },
          { title: 'Critical Priority', path: `${paths.dashboard.bugs.list}?priority=CRITICAL` },
        ],
      },
      {
        title: 'Chat',
        path: paths.dashboard.chat.root,
        icon: ICONS.support,
        roles: ['ADMIN', 'FACILITY_OWNER', 'MANAGER'],
      },
    ],
  },

  /**
   * Admin Tools
   */
  {
    subheader: 'Admin Tools',
    items: [
      {
        title: 'Reports',
        path: '/dashboard/reports',
        icon: ICONS.analytics,
        roles: ['ADMIN'], // Admin only
        children: [
          { title: 'User Activity', path: '/dashboard/reports/user-activity' },
          { title: 'Club Performance', path: '/dashboard/reports/club-performance' },
          { title: 'Revenue Analytics', path: '/dashboard/reports/revenue-analytics' },
          { title: 'System Usage', path: '/dashboard/reports/system-usage' },
        ],
      },
    ],
  },
];
